"use client";
import { Reserve } from "@/features/reserve/components/Reserve/Reserve";
import { ReserveOptions } from "@/features/reserve/components/Reserve/types";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { scheduleResourceStyling } from "@/lib/utils";
import { ReserveFormProvider } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { useRoomReservationWeb } from "@gettactic/hooks";
import {
  DelegatedPermissionRequest,
  DelegatedPermissionRequestContent
} from "./DelegatedPermissionRequest";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import React, { useCallback } from "react";

type ReserveFormProps = {
  reserveModal: boolean;
  onClose: () => void;
  wpRequestsRef?: React.RefObject<WorkplaceRequestsRef | null>;
} & ReserveOptions;

export const ReserveForm: React.FC<ReserveFormProps> = ({
  reserveModal = false,
  resourceType = "desk",
  isEmbedded = false,
  start,
  isAdvancedRecurringOpenByDefault,
  time,
  duration,
  office,
  resource,
  floorId,
  schedule,
  onClose,
  workplaceRequestItems,
  wpRequestsRef,
  attenders,
  title,
  owner,
  onSuccess
}) => {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const isParking =
    resourceType === "parking_lot" || resourceType === "parking_space";
  const customParkingVerbiage = useCustomParkingVerbiage();
  const hideImage = isParking && customParkingVerbiage !== null;

  const styling = scheduleResourceStyling(
    resourceType,
    customParkingVerbiage !== null
  );

  const {
    requireDelegatedPermission,
    msAuthUrl,
    onRoomChange,
    hasMSCalendar,
    hasGoogleCalendar
  } = useRoomReservationWeb({
    isOpen: !!reserveModal,

    isRoomReservation: resourceType === "meeting_room"
  });

  const handleConnectOutlook = useCallback(() => {
    window.open(msAuthUrl);
  }, [msAuthUrl]);

  return requireDelegatedPermission ? (
    isMd ? (
      <DelegatedPermissionRequest msAuthUrl={msAuthUrl} onClose={onClose} />
    ) : (
      <DelegatedPermissionRequestContent
        msAuthUrl={msAuthUrl}
        handleConnectOutlook={handleConnectOutlook}
      />
    )
  ) : (
    <>
      {reserveModal ? (
        <ReserveFormProvider
          resourceType={resourceType}
          isEmbedded={isEmbedded}
          start={start}
          time={time}
          duration={duration}
          office={office}
          resource={resource}
          floorId={floorId}
          schedule={schedule}
          onSuccess={() => {
            onSuccess?.();
            onClose?.();
          }}
          onRoomChange={onRoomChange}
          hasMSCalendar={hasMSCalendar}
          hasGoogleCalendar={hasGoogleCalendar}
          wpRequestsRef={wpRequestsRef}
          workplaceRequestItems={workplaceRequestItems}
          attenders={attenders}
          title={title}
          owner={owner}
          isAdvancedRecurringOpenByDefault={isAdvancedRecurringOpenByDefault}
        >
          <div className={"px-4"}>
            <Reserve onClose={onClose} />
          </div>
        </ReserveFormProvider>
      ) : null}
    </>
  );
};
